/**
 * Rileva cartelle sul Volume NAS che potrebbero già appartenere a una pratica
 * (nome che inizia con il numero pratica).
 */

// Module-level cache for NAS directory listings to avoid repeated scans
let _cachedVolumePath: string | null = null;
let _cachedDirNames: string[] | null = null;

export function getCachedDirectoryNames(volumePath: string): string[] | null {
  if (_cachedVolumePath === volumePath && _cachedDirNames) return _cachedDirNames;
  return null;
}

export function setCachedDirectoryNames(volumePath: string, names: string[]): void {
  _cachedVolumePath = volumePath;
  _cachedDirNames = names;
}

export function invalidateDirectoryCache(): void {
  _cachedVolumePath = null;
  _cachedDirNames = null;
}

/** Normalizza il numero pratica per il confronto prefisso */
export function normalizePracticeNumberPrefix(practiceNumber: string): string {
  return (practiceNumber || '').trim();
}

/**
 * true se il nome cartella è candidato per il numero pratica.
 * Richiede almeno 2 caratteri nel numero; match case-insensitive su prefisso.
 */
export function folderNameMatchesPracticePrefix(folderName: string, practiceNumberRaw: string): boolean {
  const pn = normalizePracticeNumberPrefix(practiceNumberRaw);
  if (pn.length < 2) return false;
  const name = folderName.trim();
  if (!name) return false;
  const pl = pn.toLowerCase();
  const nl = name.toLowerCase();
  if (nl.startsWith(pl)) {
    if (nl.length === pl.length) return true;
    const next = nl[pl.length];
    if (next === '_' || next === ' ' || next === '-' || next === '.') return true;
    if (/^\d+$/.test(pn)) {
      return !/\d/.test(next);
    }
    return true;
  }
  return false;
}

/** Elenco nomi cartella (solo directory) che matchano il prefisso, ordinati */
export function filterMatchingFolderNames(
  folderNames: string[],
  practiceNumber: string
): string[] {
  const out = folderNames.filter((n) => folderNameMatchesPracticePrefix(n, practiceNumber));
  return [...new Set(out)].sort((a, b) => a.localeCompare(b, 'it'));
}

/** Browser: elenca nomi delle sotto-cartelle di parentHandle */
export async function listChildDirectoryNamesBrowser(
  parentHandle: FileSystemDirectoryHandle
): Promise<string[]> {
  const names: string[] = [];
  try {
    for await (const [name, handle] of (parentHandle as any).entries()) {
      if (handle?.kind === 'directory') names.push(name);
    }
  } catch {
    /* vuoto */
  }
  return names;
}

/** Electron: elenca directory figlie tramite preload (con cache + listDirectoryNames veloce) */
export function listChildDirectoryNamesElectron(volumeUncPath: string): string[] {
  const cached = getCachedDirectoryNames(volumeUncPath);
  if (cached) return cached;

  const efs = typeof window !== 'undefined' ? window.electronFS : undefined;
  if (!efs || !volumeUncPath) return [];
  try {
    let names: string[];
    if (efs.listDirectoryNames) {
      names = efs.listDirectoryNames(volumeUncPath);
    } else {
      const entries = efs.readDirectory(volumeUncPath);
      names = entries.filter((e) => e.kind === 'directory').map((e) => e.name);
    }
    setCachedDirectoryNames(volumeUncPath, names);
    return names;
  } catch {
    return [];
  }
}

/** Combina list + filter */
export async function findMatchingPracticeFoldersBrowser(
  parentHandle: FileSystemDirectoryHandle,
  practiceNumber: string
): Promise<string[]> {
  const names = await listChildDirectoryNamesBrowser(parentHandle);
  return filterMatchingFolderNames(names, practiceNumber);
}

export function findMatchingPracticeFoldersElectron(volumeUncPath: string, practiceNumber: string): string[] {
  const names = listChildDirectoryNamesElectron(volumeUncPath);
  return filterMatchingFolderNames(names, practiceNumber);
}

const FOUR_DIGIT_PREFIX = /^(\d{4})(?=[_\s\-.]|$)/;

/**
 * Scansiona le directory in volumeUncPath (Electron) ed estrae il massimo
 * prefisso numerico a 4 cifre, restituendo il prossimo numero disponibile.
 * Ignora cartelle senza prefisso numerico valido.
 */
export function getNextPracticeNumberElectron(volumeUncPath: string): number | null {
  const names = listChildDirectoryNamesElectron(volumeUncPath);
  if (names.length === 0) return null;
  let max = -1;
  for (const name of names) {
    const m = name.match(FOUR_DIGIT_PREFIX);
    if (m) {
      const n = parseInt(m[1], 10);
      if (n > max) max = n;
    }
  }
  return max >= 0 ? max + 1 : null;
}
